import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
} from "react";

import { MaxWidthType } from "../@types";

import { useColorScheme } from "@/hooks/useColorScheme";
import { useDimensions } from "@/hooks/useDimensions";
import { usePublicLayoutStore } from "@/hooks/usePublicLayoutStore";
import { PublicLayoutType } from "@/layouts/@types";
import { CustomSection } from "@/layouts/PublicLayout/sections/CustomSection";
import { EmptySection } from "@/layouts/PublicLayout/sections/EmptySection";
import {
  ScreenshotSection,
  ScreenshotTopSection,
} from "@/layouts/PublicLayout/sections/ScreenshotSection";
import { Utils } from "@/utils";
import { cn } from "@/components/ui/utils";

function Inner({
  children,
  assignRef,
  backgroundColor = "transparent",
  //backgroundGradient = false,
  rulersOverChildren = false,
  includeLeftVerticalRuler = false,
  includeRightVerticalRuler = false,
  includeHorizontalRulers = false,
  verticalRulerColor = null,
  horizontalRulerColor = null,
  verticalRulerTopColor = null,
  verticalRulerBottomColor = null,
  includePadding = true,
  forceNoPadding = false,
  maxWidth = "lg",
  invertColors = false,
  invertedColor = null,
  innerClassName = "pl-8 pr-8 pt-4 pb-4",
  outerClassName = "",
  renderOuterChildren,
}: {
  children: ReactNode;
  assignRef: (ref: HTMLDivElement) => void;
  backgroundColor?: string;
  backgroundGradient?: boolean;
  rulersOverChildren?: boolean;
  includeLeftVerticalRuler?: boolean;
  includeRightVerticalRuler?: boolean;
  includeHorizontalRulers?: boolean;
  verticalRulerColor?: string;
  horizontalRulerColor?: string;
  verticalRulerTopColor?: string;
  verticalRulerBottomColor?: string;
  includePadding?: boolean;
  forceNoPadding?: boolean;
  maxWidth: MaxWidthType;
  invertColors?: boolean;
  invertedColor?: string;
  innerClassName?: string;
  outerClassName?: string;
  renderOuterChildren?: () => ReactNode;
}) {
  backgroundColor;
  verticalRulerColor;
  horizontalRulerColor;
  verticalRulerTopColor;
  verticalRulerBottomColor;
  invertColors;
  invertedColor;
  renderOuterChildren;

  if (verticalRulerColor === null) {
    verticalRulerColor = "var(--border)";
  }
  if (horizontalRulerColor === null) {
    horizontalRulerColor = "var(--border)";
  }
  if (verticalRulerTopColor === null) {
    verticalRulerTopColor = verticalRulerColor;
  }
  if (verticalRulerBottomColor === null) {
    verticalRulerBottomColor = verticalRulerColor;
  }
  if (forceNoPadding === true && includePadding === true) {
    includePadding = false;
  }
  const innerRef = useRef(null);

  const renderChildren = () => (
    <div className="w-full h-full">
      <div
        className={cn(
          "w-full h-full",
          innerClassName,
          !rulersOverChildren && "relative",
        )}
      >
        {children}
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "w-full relative",
        outerClassName,
        "pl-4 pr-4 md:pl-8 md:pr-8",
      )}
      ref={assignRef}
    >
      {renderOuterChildren && renderOuterChildren()}
      <div className="flex min-w-0 flex-wrap w-full flex-col items-center">
        {includeHorizontalRulers && (
          <>
            <div
              className="min-w-0 absolute left-0 right-0 h-px [background:var(--background)]"
              style={
                {
                  "--background": `repeating-linear-gradient(to right, transparent 0 4px, ${horizontalRulerColor} 4px 8px) 2px 100% no-repeat`,
                } as CSSProperties
              }
            />
          </>
        )}
        {maxWidth === null && renderChildren()}
        <div
          ref={innerRef}
          className={cn(
            "min-w-0",
            "w-full",
            maxWidth === "xs"
              ? "max-w-[xs]"
              : maxWidth === "sm"
                ? "max-w-100"
                : maxWidth === "md"
                  ? "max-w-180"
                  : maxWidth === "lg"
                    ? "max-w-250"
                    : maxWidth === "xl"
                      ? "max-w-350"
                      : "",
            "[border:0px_solid] relative",
          )}
        >
          {maxWidth !== null && rulersOverChildren && renderChildren()}
          {(includeLeftVerticalRuler || includeRightVerticalRuler) && (
            <>
              {/* Vertical */}
              {includeLeftVerticalRuler && (
                <div
                  className="absolute -left-px top-0 w-px h-full [background:var(--background)]"
                  style={
                    {
                      "--background": `linear-gradient(to bottom, ${verticalRulerTopColor}, ${verticalRulerBottomColor})`,
                    } as CSSProperties
                  }
                />
              )}
              {includeRightVerticalRuler && (
                <div
                  className="absolute -right-px top-0 w-px h-full [background:var(--background)]"
                  style={
                    {
                      "--background": `linear-gradient(to bottom, ${verticalRulerTopColor}, ${verticalRulerBottomColor})`,
                    } as CSSProperties
                  }
                />
              )}
            </>
          )}
          {maxWidth !== null && !rulersOverChildren && renderChildren()}
        </div>
      </div>
    </div>
  );
}

export function PublicLayout({
  layout,
  maxWidth,
}: {
  layout: PublicLayoutType;
  maxWidth: MaxWidthType;
}) {
  const { width } = useDimensions();
  const initialColorScheme = useRef(null);
  const { colorScheme, setColorScheme } = useColorScheme();

  useLayoutEffect(() => {
    if (initialColorScheme.current === null) {
      initialColorScheme.current = colorScheme;
    }
    setColorScheme("light");
    return () => {
      setColorScheme(initialColorScheme.current);
    };
  }, []);

  const refs = useRef([]);
  const idToIdxMap = useRef({});
  const idxToIdList = useRef<string[]>([]);
  let currentIndex = 0;

  const updateIdToIdxMap = usePublicLayoutStore(
    (state) => state.updateIdToIdxMap,
  );
  const updateIdxToIdList = usePublicLayoutStore(
    (state) => state.updateIdxToIdList,
  );
  const updateOffsetsById = usePublicLayoutStore(
    (state) => state.updateOffsetsById,
  );

  const getAssignRef = (id: string) => {
    return (ref: HTMLDivElement) => {
      if (!ref) return;
      if (id in refs.current) return;
      refs.current[id] = ref;
      idToIdxMap.current[id] = currentIndex++;
      idxToIdList.current.push(id);
    };
  };

  const updateOffsets = useCallback(() => {
    let offset = 0;
    const newOffsets = {};
    idxToIdList.current.forEach((id, idx) => {
      const prevId = idxToIdList.current?.[idx - 1];
      const prevRef = refs.current?.[prevId];

      offset += prevRef?.clientHeight | 0;
      newOffsets[id] = offset;
    });
    updateOffsetsById({ ...newOffsets });
    updateIdToIdxMap({ ...idToIdxMap.current });
    updateIdxToIdList([...idxToIdList.current]);
  }, [updateIdToIdxMap, updateIdxToIdList, updateOffsetsById]); // Add an empty array as the second argument

  useEffect(() => {
    updateOffsets();
  }, [width, layout, updateOffsets]);

  // Note: Some weird bug where the ref clientHeight is inaccurate on first render for focusSection
  useEffect(() => {
    setTimeout(() => {
      updateOffsets();
    }, 100);
  }, [updateOffsets]);

  return (
    <div className="flex min-w-0 flex-wrap flex-row">
      {layout.content.map((sectionLayout, index) => {
        const verticalRulerBottomColor = sectionLayout.verticalRulerBottomColor;
        const verticalRulerTopColor = sectionLayout.verticalRulerTopColor;
        const horizontalRulerColor = sectionLayout.horizontalRulerColor;
        const backgroundColor = sectionLayout.backgroundColor;
        const includeLeftVerticalRuler = sectionLayout.includeLeftVerticalRuler;
        const includeRightVerticalRuler =
          sectionLayout.includeRightVerticalRuler;
        const includeHorizontalRuler = sectionLayout.includeHorizontalRuler;
        const invertColors = sectionLayout.invertColors;
        const invertedColor = sectionLayout.invertedColor;

        return (
          <React.Fragment key={Utils.getListItemKey(index)}>
            {sectionLayout.type === "custom" && (
              <Inner
                assignRef={getAssignRef(index + "-custom-1")}
                maxWidth={maxWidth}
                backgroundColor={backgroundColor}
                verticalRulerTopColor={verticalRulerTopColor}
                verticalRulerBottomColor={verticalRulerBottomColor}
                horizontalRulerColor={horizontalRulerColor}
                includeLeftVerticalRuler={includeLeftVerticalRuler}
                includeRightVerticalRuler={includeRightVerticalRuler}
                includeHorizontalRulers={includeHorizontalRuler}
                innerClassName={sectionLayout?.innerClassName}
                outerClassName={sectionLayout?.outerClassName}
                includePadding={sectionLayout?.includePadding}
                forceNoPadding={sectionLayout?.forceNoPadding}
                invertColors={invertColors}
                invertedColor={invertedColor}
                renderOuterChildren={sectionLayout?.renderOuterChildren}
              >
                <CustomSection layout={sectionLayout}>
                  {sectionLayout.renderChildren(sectionLayout)}
                </CustomSection>
              </Inner>
            )}
            {sectionLayout.type === "empty" && (
              <>
                <Inner
                  assignRef={getAssignRef(index + "-empty-1")}
                  maxWidth={maxWidth}
                  backgroundColor={backgroundColor}
                  verticalRulerTopColor={verticalRulerTopColor}
                  verticalRulerBottomColor={verticalRulerBottomColor}
                  horizontalRulerColor={horizontalRulerColor}
                  includeLeftVerticalRuler={includeLeftVerticalRuler}
                  includeRightVerticalRuler={includeRightVerticalRuler}
                  includeHorizontalRulers={includeHorizontalRuler}
                  innerClassName={sectionLayout?.innerClassName}
                  outerClassName={sectionLayout?.outerClassName}
                  invertColors={invertColors}
                  invertedColor={invertedColor}
                >
                  <EmptySection
                    height={sectionLayout.height}
                    heightPx={sectionLayout.heightPx}
                  />
                </Inner>
              </>
            )}
            {sectionLayout.type === "screenshot" && (
              <>
                <Inner
                  assignRef={getAssignRef(index + "-screenshot-1")}
                  maxWidth={maxWidth}
                  backgroundColor={backgroundColor}
                  verticalRulerTopColor={verticalRulerTopColor}
                  includeLeftVerticalRuler={includeLeftVerticalRuler}
                  includeRightVerticalRuler={includeRightVerticalRuler}
                  includeHorizontalRulers={false}
                  horizontalRulerColor={horizontalRulerColor}
                  includePadding={true}
                  rulersOverChildren={false}
                  invertColors={invertColors}
                  invertedColor={invertedColor}
                  innerClassName="pt-4 pb-4"
                >
                  <ScreenshotTopSection
                    layout={sectionLayout}
                    maxWidth={maxWidth}
                  />
                </Inner>
                <Inner
                  assignRef={getAssignRef(index + "-screenshot-2")}
                  maxWidth={maxWidth}
                  backgroundColor={backgroundColor}
                  verticalRulerTopColor={verticalRulerTopColor}
                  includeLeftVerticalRuler={includeLeftVerticalRuler}
                  includeRightVerticalRuler={includeRightVerticalRuler}
                  includeHorizontalRulers={includeHorizontalRuler}
                  horizontalRulerColor={horizontalRulerColor}
                  includePadding={false}
                  rulersOverChildren={false}
                  invertColors={invertColors}
                  invertedColor={invertedColor}
                  outerClassName={cn(sectionLayout.outerClassName, "pt-0 pb-0")}
                  innerClassName="pl-0 pr-0 pt-0 pb-0"
                >
                  <ScreenshotSection
                    layout={sectionLayout}
                    maxWidth={maxWidth}
                  />
                </Inner>
              </>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
